import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import HeatmapTable from 'components/Heatmap'
import Select from 'components/Inputs/Select'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import DateInput from 'components/Inputs/DateInput'
import { DateTime } from 'luxon'

import {
  Column,
  LoadingContainer,
  FilterContainer,
  InfoText
} from '../styles'

export default function Heatmap () {
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const [loadingData, setLoadingData] = useState (true)
  const [loadingHeatMap, setLoadingHeatMap] = useState (true)
  const [professionals, setProfessionals] = useState ([])
  const [serviceCategories, setServiceCategories] = useState ([])
  const [selectedProfessional, setSelectedProfessional] = useState ({
    label: 'Todos os profissionais', value: 'all_professionals'
  })
  const [selectedCategory, setSelectedCategory] = useState ({
    label: 'Todas as categorias', value: 'all_categories'
  })
  const [heatMapData, setHeatMapData] = useState (null)
  const [dateRange, setDateRange] = useState ([
    DateTime.now ().minus ({ days: 7 }).toISODate (),
    DateTime.now ().toISODate ()
  ])

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }
  
  useEffect (() => {
    getData ()
  }, [])

  useEffect (() => {
    if (dateRange && selectedProfessional && selectedCategory) getHeatMap ()
  }, [dateRange, selectedProfessional, selectedCategory])

  const getData = async () => {
    try {
      const { data: categories } = await cutileiApi.get ('/service_categories', requestConfig)
      const { data: professionals } = await cutileiApi.get (
        `/businesses/${businessId}/professionals?access_level=service&deleted=false`,
        requestConfig
      )

      const categoryOptions = [
        { label: 'Todas as categorias', value: 'all_categories' },
        ...categories.map (category => ({
          label: category.name,
          value: category.id
        }))
      ]

      const professionalOptions = [
        { label: 'Todos os profissionais', value: 'all_professionals' },
        ...professionals.map (professional => ({
          label: professional.nickname,
          value: professional.id
        }))
      ]

      setServiceCategories (categoryOptions)
      setProfessionals (professionalOptions)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoadingData (false)
    }
  }

  const getHeatMap = async () => {
    setLoadingHeatMap (true)
    try {
      const startDate = dateRange[0]
      const endDate = dateRange[1]
      let filters = `start_date=${startDate}&end_date=${endDate}`

      if (selectedProfessional)
        filters += `&professional_id=${selectedProfessional.value}`

      if (selectedCategory)
        filters += `&category_id=${selectedCategory.value}`

      const { data: heatMapData } = await cutileiApi.get (
        `/reports/heat_map?business_id=${businessId}&${filters}`,
        requestConfig
      )

      setHeatMapData (heatMapData)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoadingHeatMap (false)
    }
  }

  return loadingData ? <Loading/> : (
    <Column>
      <FilterContainer style={{marginBottom: 15}}>
        <DateInput
          value={dateRange}
          selectRange={true}
          minRangeDays={7}
          maxRangeDays={31}
          onChange={setDateRange}
          style={{width: 250}}
        />
        <Select
          placeholder='Profissional...'
          value={selectedProfessional}
          options={professionals}
          onChange={(_, option) => setSelectedProfessional (option)}
          containerStyles={{width: 250}}
        />
        {selectedProfessional.value === 'all_professionals' && (
          <Select
            placeholder='Categoria de serviço...'
            value={selectedCategory}
            options={serviceCategories}
            onChange={(_, option) => setSelectedCategory (option)}
            containerStyles={{width: 250}}
          />
        )}
      </FilterContainer>
      {loadingHeatMap ? (
        <LoadingContainer>
          <Loading/>
          <InfoText>Gerando mapa de calor...</InfoText>
        </LoadingContainer>
      ) : (
        <HeatmapTable data={heatMapData}/>
      )}
    </Column>
  )
}
